import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, BookOpen, CheckCircle, Loader2, Trash2, XCircle } from 'lucide-react';
import {
  LearningShell,
  PageIntro,
  MetricStrip,
  ToneBadge,
} from '../features/learning-space/components';
import { useLearningViewer } from '../features/learning-space/useLearningViewer';
import { buildLearningRouteScope } from '@examflow/learning-space';
import {
  buildRetentionMistakeBookPrimaryAction,
  buildRetentionToolBackAction,
  resolveDominantMistakeSkillId,
} from '../features/learning-space/retentionToolsModel';
import { api, type Mistake, type MistakeQuestion, type MistakeStats } from '../lib/api';
import { cn, LoadingSpinner, useConfirm } from '@examflow/ui';
import { FilterBar } from '../features/mistake-filter/components/FilterBar';
import { useMistakeFilters, useFilteredMistakes } from '../features/mistake-filter/hooks';
import {
  MistakeRetryButton,
  PracticeSimilarButton,
} from '../components/mistake/MistakeRetryButton';

type TabId = 'all' | 'due';

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('zh-CN', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatAnswer(answer: string | string[] | null | undefined): string {
  if (answer == null) return '—';
  if (Array.isArray(answer)) return answer.join(', ');
  return String(answer);
}

function isListeningQuestion(question: MistakeQuestion): boolean {
  if (question.section === 'listening') return true;
  return Array.isArray(question.stimulus) && question.stimulus.includes('audio');
}

function answerMatchesOption(
  answer: string | string[] | null | undefined,
  optionId: string
): boolean {
  if (answer == null) return false;
  if (Array.isArray(answer)) return answer.includes(optionId);
  return answer === optionId || answer.split(',').map((s) => s.trim()).includes(optionId);
}

function isCorrectOption(question: MistakeQuestion, optionId: string): boolean {
  if (question.options?.some((o) => o.id === optionId && o.isCorrect)) return true;
  return answerMatchesOption(question.correctAnswer, optionId);
}

function QuestionContent({ mistake }: { mistake: Mistake }) {
  const question = mistake.question;
  const stem = question.content?.trim();
  const options = question.options ?? [];

  return (
    <div className="space-y-3">
      <div>
        <div className="text-xs font-semibold text-slate-500 mb-1">题目内容</div>
        {stem ? (
          <div className="text-sm text-[#111827] whitespace-pre-wrap">{stem}</div>
        ) : (
          <div className="rounded-[6px] border border-dashed border-[#dbe4d8] bg-[#f8faf6] px-3 py-2 text-sm text-slate-500">
            题干暂不可用。可直接练习本题；看解析请回到训练结果页。
          </div>
        )}
      </div>
      {question.passage && !isListeningQuestion(question) ? (
        <div>
          <div className="text-xs font-semibold text-slate-500 mb-1">阅读材料</div>
          <div className="text-sm text-slate-700 whitespace-pre-wrap line-clamp-6">{question.passage}</div>
        </div>
      ) : null}
      {options.length > 0 ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {options.map((option) => {
            const isUserAnswer = answerMatchesOption(mistake.userAnswer, option.id);
            const correct = isCorrectOption(question, option.id);
            return (
              <div
                key={option.id}
                className={cn(
                  'px-3 py-2 rounded-[6px] text-sm border',
                  correct
                    ? 'bg-[#e5f4f2] border-[#b9dcd8] text-[#0f8b8d] font-semibold'
                    : isUserAnswer
                      ? 'bg-[#fdecea] border-[#efbbb4] text-[#c94a3a]'
                      : 'bg-[#f8faf6] border-[#dbe4d8] text-slate-600'
                )}
              >
                <span className="font-medium mr-2">{option.id}.</span>
                {option.content}
                {correct ? <CheckCircle className="inline-block h-3 w-3 ml-2" /> : null}
                {isUserAnswer && !correct ? (
                  <XCircle className="inline-block h-3 w-3 ml-2" />
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
      {question.explanation ? (
        <div className="rounded-[6px] border border-[#dbe4d8] bg-[#f8faf6] px-3 py-2">
          <div className="text-xs font-semibold text-slate-500 mb-1">解析</div>
          <div className="text-sm text-slate-700 whitespace-pre-wrap">{question.explanation}</div>
        </div>
      ) : null}
      {question.type ? (
        <div className="text-xs text-slate-500">
          <span className="font-medium">题型：</span>
          {question.type}
        </div>
      ) : null}
    </div>
  );
}

function MistakeCard({
  mistake,
  onReview,
  onDelete,
  isReviewing,
  similarCount = 5,
}: {
  mistake: Mistake;
  onReview: (remembered: boolean) => void;
  onDelete: () => void | Promise<void>;
  isReviewing: boolean;
  similarCount?: number;
}) {
  const question = mistake.question;
  const correctAnswer = formatAnswer(
    question.correctAnswer ??
      question.options?.find((o) => o.isCorrect)?.id ??
      null
  );

  return (
    <div className="rounded-[8px] border border-[#dbe4d8] bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <ToneBadge
              tone={
                mistake.wrongCount > 3 ? 'danger' : mistake.wrongCount > 1 ? 'warning' : 'neutral'
              }
            >
              错误 {mistake.wrongCount} 次
            </ToneBadge>
            {mistake.nextReviewTime && new Date(mistake.nextReviewTime) <= new Date() ? (
              <ToneBadge tone="current">待复习</ToneBadge>
            ) : null}
            {question.section ? (
              <span className="text-xs text-slate-500 border border-[#dbe4d8] rounded-full px-2 py-0.5">
                {question.section}
              </span>
            ) : null}
            {question.level ? (
              <span className="text-xs font-medium text-primary-600 border border-primary-200 rounded-full px-2 py-0.5 bg-primary-50">
                {question.level}
              </span>
            ) : null}
          </div>
          <QuestionContent mistake={mistake} />
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span>
              正确答案：<span className="font-semibold text-[#0f8b8d]">{correctAnswer}</span>
            </span>
            <span>你的答案：{formatAnswer(mistake.userAnswer)}</span>
            <span>最近错误：{formatDate(mistake.lastWrongTime)}</span>
            {mistake.reviewCount > 0 ? <span>复习 {mistake.reviewCount} 次</span> : null}
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0 w-36">
          <div className="flex flex-col gap-1.5">
            <MistakeRetryButton mistake={mistake} variant="compact" />
            {similarCount > 1 ? (
              <PracticeSimilarButton mistake={mistake} similarCount={similarCount} />
            ) : null}
          </div>
          <div className="border-t border-[#dbe4d8] pt-2 mt-1 space-y-1.5">
            <p className="text-[10px] leading-4 text-slate-400 px-0.5">
              仅更新复习计划，不写能力诊断
            </p>
            <button
              type="button"
              onClick={() => onReview(true)}
              disabled={isReviewing}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-[6px] bg-[#e5f4f2] text-[#0f8b8d] text-sm font-semibold border border-[#b9dcd8] hover:bg-[#d0ebE8] disabled:opacity-50 transition-colors"
            >
              {isReviewing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCircle className="h-3.5 w-3.5" />
              )}
              记住了
            </button>
            <button
              type="button"
              onClick={() => onReview(false)}
              disabled={isReviewing}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-[6px] bg-[#fff2d8] text-[#b86b16] text-sm font-semibold border border-[#f0d293] hover:bg-[#ffe9b8] disabled:opacity-50 transition-colors"
            >
              {isReviewing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <AlertTriangle className="h-3.5 w-3.5" />
              )}
              再想想
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={isReviewing}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-[6px] bg-white text-[#c94a3a] text-sm font-semibold border border-[#efbbb4] hover:bg-[#fdecea] disabled:opacity-50 transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
              删除
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MistakeBookPage() {
  const confirm = useConfirm();
  const [searchParams] = useSearchParams();
  const routeScope = buildLearningRouteScope(searchParams);
  const viewer = useLearningViewer(routeScope);
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>('all');

  const {
    filters,
    setExamType,
    setSection,
    setDifficulty,
    setSortBy,
    toggleSortDirection,
    resetFilters,
    hasActiveFilters,
  } = useMistakeFilters();

  const {
    data: statsData,
    isLoading: statsLoading,
  } = useQuery({
    queryKey: ['mistake-stats'],
    queryFn: () => api.mistakes.stats() as Promise<MistakeStats>,
  });

  const listParams =
    routeScope.examType != null ? { examType: routeScope.examType } : undefined;

  const {
    data: allMistakes,
    isLoading: allLoading,
    error: allError,
  } = useQuery({
    queryKey: ['mistakes', 'all', routeScope.examType ?? 'all'],
    queryFn: () => api.mistakes.list(listParams) as Promise<{ mistakes: Mistake[] }>,
  });

  const {
    data: dueMistakes,
    isLoading: dueLoading,
    error: dueError,
  } = useQuery({
    queryKey: ['mistakes', 'due'],
    queryFn: () => api.mistakes.getDue() as Promise<{ mistakes: Mistake[] }>,
  });

  const allMistakesList = allMistakes?.mistakes ?? [];
  const dueMistakesList = (dueMistakes?.mistakes ?? []).filter((m) => {
    if (!routeScope.examType) return true;
    return m.question.examType === routeScope.examType;
  });
  const activeMistakesBase = activeTab === 'all' ? allMistakesList : dueMistakesList;
  const { filteredMistakes, examTypeOptions, sectionOptions, difficultyOptions } =
    useFilteredMistakes(activeMistakesBase, filters);

  const reviewMutation = useMutation({
    mutationFn: ({ id, remembered }: { id: string; remembered: boolean }) =>
      api.mistakes.review(id, remembered),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mistakes'] });
      void queryClient.invalidateQueries({ queryKey: ['mistake-stats'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.mistakes.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mistakes'] });
      void queryClient.invalidateQueries({ queryKey: ['mistake-stats'] });
    },
  });

  const isLoading = activeTab === 'all' ? allLoading : dueLoading;
  const error = activeTab === 'all' ? allError : dueError;
  const dueCount = statsData?.dueCount ?? 0;
  const focusSkillId = resolveDominantMistakeSkillId(dueMistakesList);

  const metrics = [
    {
      label: '错题总数',
      value: statsLoading ? '…' : String(statsData?.totalMistakes ?? 0),
      tone: (statsData?.totalMistakes ?? 0) > 0 ? ('warning' as const) : ('success' as const),
      caption: '累计记录',
    },
    {
      label: '待复习',
      value: statsLoading ? '…' : String(statsData?.dueCount ?? 0),
      tone: dueCount > 0 ? ('current' as const) : ('success' as const),
      caption: '等待复习',
    },
    {
      label: '已掌握',
      value: statsLoading ? '…' : String(statsData?.masteredCount ?? 0),
      tone: 'success' as const,
      caption: '复习≥3 且未到期',
    },
    {
      label: '复习次数',
      value: statsLoading ? '…' : String(statsData?.totalReviews ?? 0),
      tone: 'neutral' as const,
      caption: '累计复习',
    },
  ];

  return (
    <LearningShell viewer={viewer} activeNav="workspace" statusLabel="留存工具">
      <PageIntro
        eyebrow="留存工具 · 非主闭环"
        title="错题本"
        subtitle="这里以归档浏览为主。到期项可按模块开短训；看解析请回到训练结果页。"
        secondaryAction={buildRetentionToolBackAction(routeScope)}
        primaryAction={buildRetentionMistakeBookPrimaryAction(routeScope, {
          dueCount,
          focusSkillId,
        })}
      />
      <MetricStrip metrics={metrics} />

      <div className="mt-7">
        <div className="flex items-center gap-1 border-b border-[#dbe4d8]">
          <button
            type="button"
            onClick={() => setActiveTab('all')}
            className={cn(
              'px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors',
              activeTab === 'all'
                ? 'border-[#1f4fd8] text-[#1f4fd8]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            )}
          >
            全部错题
            {allMistakes?.mistakes ? (
              <span className="ml-1.5 text-xs font-medium text-slate-400">
                ({allMistakes.mistakes.length})
              </span>
            ) : null}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('due')}
            className={cn(
              'px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors',
              activeTab === 'due'
                ? 'border-[#1f4fd8] text-[#1f4fd8]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            )}
          >
            待复习
            {dueMistakesList.length > 0 ? (
              <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-[#c94a3a] text-white rounded-full">
                {dueMistakesList.length}
              </span>
            ) : null}
          </button>
        </div>

        {!isLoading && !error && activeMistakesBase.length > 0 ? (
          <FilterBar
            filters={filters}
            examTypeOptions={examTypeOptions}
            sectionOptions={sectionOptions}
            difficultyOptions={difficultyOptions}
            onExamTypeChange={setExamType}
            onSectionChange={setSection}
            onDifficultyChange={setDifficulty}
            onSortByChange={setSortBy}
            onSortDirectionToggle={toggleSortDirection}
            onReset={resetFilters}
            hasActiveFilters={hasActiveFilters}
          />
        ) : null}

        <div className="mt-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner />
              <span className="ml-3 text-sm text-slate-500">加载中...</span>
            </div>
          ) : error ? (
            <div className="rounded-[8px] border border-[#efbbb4] bg-[#fdecea] px-4 py-5 text-sm font-semibold text-[#c94a3a] text-center">
              加载失败，请稍后重试
            </div>
          ) : filteredMistakes.length === 0 ? (
            <div className="rounded-[8px] border border-[#dbe4d8] bg-white px-4 py-16 text-center">
              <BookOpen className="h-12 w-12 mx-auto text-slate-300" />
              <h3 className="mt-4 text-lg font-bold text-slate-700">
                {hasActiveFilters ? '没有符合条件的错题' : '暂无错题'}
              </h3>
              <p className="mt-2 text-sm text-slate-500">
                {hasActiveFilters
                  ? '请尝试调整筛选条件'
                  : activeTab === 'due'
                    ? '没有需要复习的错题，继续保持！'
                    : '完成练习后，答错的题目会自动收录到这里'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredMistakes.map((mistake) => (
                <MistakeCard
                  key={mistake.id}
                  mistake={mistake}
                  onReview={(remembered) => {
                    reviewMutation.mutate({ id: mistake.id, remembered });
                  }}
                  onDelete={async () => {
                    const ok = await confirm({
                      title: '删除错题',
                      message: '确定要删除这条错题记录吗？',
                      confirmLabel: '删除',
                      cancelLabel: '取消',
                      variant: 'danger',
                    });
                    if (ok) {
                      deleteMutation.mutate(mistake.id);
                    }
                  }}
                  isReviewing={reviewMutation.isPending || deleteMutation.isPending}
                  similarCount={
                    sectionOptions.find((s) => s.value === mistake.question.section)?.count ?? 5
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </LearningShell>
  );
}
